import { useEffect, useMemo, useRef, useState } from 'react';
import {
	FaShareAlt,
	FaWhatsapp,
	FaFacebookF,
	FaLink,
	FaRegEnvelope,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { encode } from 'uqr';
import { SharePopover, ShareTrigger } from '@quizzach/styles';

interface ShareButtonProps {
	shareData: {
		text: string;
		url: string;
	};
	className?: string;
	/** Render only the share icon (no label); icon inherits currentColor. */
	iconOnly?: boolean;
	iconSize?: number;
	/** Which way the desktop popover opens relative to the trigger. */
	popoverPlacement?: 'up' | 'down';
}

/** QR of the share url as inline SVG (one path, dark modules on light plate). */
const ShareQr = ({ url }: { url: string }) => {
	const { size, path } = useMemo(() => {
		const qr = encode(url, { border: 0 });
		let d = '';
		qr.data.forEach((row, y) =>
			row.forEach((on, x) => {
				if (on) d += `M${x} ${y}h1v1h-1z`;
			})
		);
		return { size: qr.size, path: d };
	}, [url]);

	return (
		<svg viewBox={`0 0 ${size} ${size}`} aria-hidden='true'>
			<path d={path} fill='#141414' />
		</svg>
	);
};

export const ShareButton: React.FC<ShareButtonProps> = ({
	shareData,
	className,
	iconOnly,
	iconSize,
	popoverPlacement = 'up',
}) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [copied, setCopied] = useState(false);
	const wrapRef = useRef<HTMLDivElement>(null);

	const shareMessage = `${shareData.text}\n\n${shareData.url}`;

	const copyLink = async () => {
		try {
			await navigator.clipboard.writeText(shareMessage);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error('Share link error', err);
		}
	};

	const handleShareClick = async () => {
		// na mobilnom native share, na desktopu popover s opcijama
		if (navigator.share && window.innerWidth < 1080) {
			try {
				await navigator.share(shareData);
			} catch (err) {
				console.error('Share link error', err);
			}
		} else {
			setOpen((wasOpen) => !wasOpen);
		}
	};

	useEffect(() => {
		if (!open) return;

		const onDocClick = (e: MouseEvent) => {
			if (!wrapRef.current?.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', onDocClick);
		document.addEventListener('keydown', onKey);

		return () => {
			document.removeEventListener('mousedown', onDocClick);
			document.removeEventListener('keydown', onKey);
		};
	}, [open]);

	if (iconOnly) {
		return (
			<button
				type='button'
				className={className}
				aria-label={t('common.share.shareLink')}
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					background: 'none',
					border: 'none',
					padding: 0,
					color: 'inherit',
					cursor: 'pointer',
				}}
				onClick={handleShareClick}
			>
				<FaShareAlt size={iconSize ?? 20} />
			</button>
		);
	}

	return (
		<div
			ref={wrapRef}
			className={className}
			style={{
				position: 'relative',
				display: 'flex',
				justifyContent: 'flex-end',
				alignItems: 'center',
			}}
		>
			<ShareTrigger type='button' onClick={handleShareClick}>
				<FaShareAlt size={14} />
				{t('common.share.shareLink')}
			</ShareTrigger>
			{open && (
				<SharePopover $placement={popoverPlacement}>
					<div className='opts'>
						<button
							type='button'
							className={copied ? 'row copy copied' : 'row copy'}
							onClick={copyLink}
						>
							<FaLink />
							{t('common.share.copyLink')}
							<span className='done'>{t('common.share.copied')}</span>
						</button>
						<span className='sep' />
						<a
							className='row wa'
							href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
							target='_blank'
							rel='noopener noreferrer'
						>
							<FaWhatsapp />
							WhatsApp
						</a>
						<a
							className='row'
							href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
								shareData.url
							)}`}
							target='_blank'
							rel='noopener noreferrer'
						>
							<FaFacebookF />
							Facebook
						</a>
						<a
							className='row'
							href={`mailto:?subject=${encodeURIComponent(
								shareData.text
							)}&body=${encodeURIComponent(shareMessage)}`}
						>
							<FaRegEnvelope />
							e-mail
						</a>
					</div>
					<div className='qr'>
						<ShareQr url={shareData.url} />
						<span className='cap'>{t('common.share.qrCaption')}</span>
					</div>
				</SharePopover>
			)}
		</div>
	);
};
